import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
// src/contexts/StabilizedContexts.tsx
// SOLUTION: Prevent excessive re-renders while maintaining assignment persistence
import { useToast } from './ToastContext';
import { useData } from './DataContext'; // ✅ FIXED: Correct import path
import { useDatabase } from './DatabaseContext';
import { useAuth } from './AuthContext';
import { supabase } from '../supabase/config';
import { setLineAssignment, setLineAssignmentsBulk } from '../services/lineAssignmentService';
import { StaffAllocationGrid, ClassAllocation } from '../types/grid';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT, DEFAULT_MINUTES_PER_LINE } from '../types/schoolConfig';
import { computeAvailableMinutes, computeUtilisation, computeAllocatedMinutes } from '../utils/teacherLoad';
import { transformGridRow } from '../utils/transformGrid';
import { useGridsData } from './GridsDataContext';
import { useAcademicYear } from './AcademicYearContext';
// Stabilized Grid Context - Single Firebase listener, optimistic updates
interface StabilizedGridContextType {
  grids: StaffAllocationGrid[];
  activeGrid: StaffAllocationGrid | null;
  loading: boolean;
  setActiveGrid: (gridId: string) => void;
  updateGrid: (gridId: string, updates: Partial<StaffAllocationGrid>) => Promise<void>;
  refetchGrid: (gridId: string) => Promise<void>;
}
const StabilizedGridContext = createContext<StabilizedGridContextType | undefined>(undefined);
export const StabilizedGridProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    addToast
  } = useToast();
  // Use Supabase instead of contextDb/defaultDb
  const [grids, setGrids] = useState<StaffAllocationGrid[]>([]);
  const [activeGrid, setActiveGridState] = useState<StaffAllocationGrid | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Prevent multiple initializations - critical for stability
  const isInitialized = useRef(false);
  const updateInProgress = useRef(false);
  const lastUpdateTime = useRef(0);
  const allowNextSnapshot = useRef(false);
  // Consume the shared grids data source (one fetch + one subscription, owned by
  // GridsDataProvider) instead of running our own. Derive this context's grids
  // from the raw rows using the canonical transform, ordered by name (preserving
  // the previous `.order('name')`). Mutations/refetchGrid below still update local
  // state optimistically, exactly as before.
  const { rawGrids, loading: gridsDataLoading } = useGridsData();
  useEffect(() => {
    isInitialized.current = true;
    const gridsData: StaffAllocationGrid[] = (rawGrids || [])
      .map(item => transformGridRow(item))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setGrids(gridsData);
    if (!activeGrid) {
      if (gridsData.length > 0) setActiveGridState(gridsData[0]);
    } else {
      // Re-sync the open grid to the refreshed data so edits made elsewhere
      // (e.g. via the wizard / class list) show without a page reload. rawGrids
      // only changes on an explicit refetch after a mutation, so this doesn't
      // fight the editor's own optimistic updates during normal cell edits.
      const updated = gridsData.find(g => g.id === activeGrid.id);
      if (updated) setActiveGridState(updated);
    }
    setLoading(gridsDataLoading);
    // activeGrid intentionally omitted from deps: this reacts to shared-data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawGrids, gridsDataLoading]);
  const setActiveGrid = useCallback((gridId: string) => {
    const grid = grids.find(g => g.id === gridId);
    if (grid) {
      setActiveGridState(grid);
    }
  }, [grids]);
  
  // Manual refetch function to bypass listener issues - DEFINED FIRST
  const refetchGrid = useCallback(async (gridId: string) => {

    try {
      const { data, error } = await supabase
        .from('grids')
        .select('*')
        .eq('id', gridId)
        .single();
        
      if (error) {
        console.error('❌ StabilizedGrid: Error refetching grid:', error);
        throw error;
      }
      
      if (data) {
        // Use the canonical transform so a refetched grid matches the initial
        // load exactly, then ensure every class has a lineAssignments object.
        const base = transformGridRow(data);
        const freshGrid: StaffAllocationGrid = {
          ...base,
          classes: base.classes.map((cls: ClassAllocation) => ({
            ...cls,
            lineAssignments: cls.lineAssignments || {},
          })),
        };

        setGrids(prev => prev.map(g => g.id === gridId ? freshGrid : g));
        if (activeGrid?.id === gridId) {
          setActiveGridState(freshGrid);
        }
      }
    } catch (error) {
      console.error('❌ StabilizedGrid: Manual refetch failed:', error);
      throw error;
    }
  }, [activeGrid, setGrids, setActiveGridState]);
  
  // SIMPLIFIED updateGrid for debugging
  const updateGrid = useCallback(async (gridId: string, updates: Partial<StaffAllocationGrid>) => {
    try {
      // Clean updates for PostgreSQL (remove undefined values)
      const cleanUpdates = JSON.parse(JSON.stringify(updates, (key, value) => value === undefined ? null : value));

      // Get current grid data to preserve structure (needed for JSONB columns)
      const { data: currentGrid } = await supabase
        .from('grids')
        .select('grid_data, settings, version')
        .eq('id', gridId)
        .single();

      const currentGridData = currentGrid?.grid_data || {};
      const currentSettings = currentGrid?.settings || {};
      const baseVersion = (currentGrid as any)?.version as number | null | undefined;

      // Convert field names for PostgreSQL schema
      const supabaseUpdates: any = {};
      if (cleanUpdates.name !== undefined) supabaseUpdates.name = cleanUpdates.name;
      if (cleanUpdates.classes !== undefined) supabaseUpdates.classes = cleanUpdates.classes;
      if (cleanUpdates.curriculumAreas !== undefined) supabaseUpdates.curriculum_areas = cleanUpdates.curriculumAreas;

      // Handle grid_data field updates (lines, semesters, and classes go in JSONB column)
      if (cleanUpdates.lines !== undefined || cleanUpdates.semesters !== undefined || cleanUpdates.classes !== undefined) {
        supabaseUpdates.grid_data = {
          ...currentGridData,
          ...(cleanUpdates.lines !== undefined && { lines: cleanUpdates.lines }),
          ...(cleanUpdates.semesters !== undefined && { semesters: cleanUpdates.semesters }),
          ...(cleanUpdates.classes !== undefined && { classes: cleanUpdates.classes })
        };

        // Also update settings field for consistency
        supabaseUpdates.settings = {
          ...currentSettings,
          ...(cleanUpdates.lines !== undefined && { lines: cleanUpdates.lines }),
          ...(cleanUpdates.semesters !== undefined && { study_periods: cleanUpdates.semesters })
        };
      }

      supabaseUpdates.updated_at = new Date().toISOString();

      // Direct Supabase update, scoped to the version we just read. This is a
      // whole-array write, so if anything changed the grid in between, losing
      // that write is the lesser evil — silently overwriting it is what
      // destroyed 212 assignments in Sep 2026 (issue #218). Cell assignments
      // no longer come through here at all; they use pit.set_line_assignment.
      let query = supabase.from('grids').update(supabaseUpdates).eq('id', gridId);
      if (typeof baseVersion === 'number') {
        query = query.eq('version', baseVersion);
      }
      const { data: updatedRows, error } = await query.select('id');

      if (!error && typeof baseVersion === 'number' && (updatedRows?.length ?? 0) === 0) {
        await refetchGrid(gridId);
        throw new Error(
          'This grid was changed by someone else while you were editing. Your change was not saved — the latest version has been reloaded, please try again.'
        );
      }

      if (error) {
        console.error('❌ StabilizedGrid: Supabase update error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      // Immediately refetch the grid to update local state
      await refetchGrid(gridId);

    } catch (error) {
      console.error('❌ StabilizedGrid: SIMPLIFIED - Database update failed:', error);
      addToast('Assignment failed to save to database', 'error');
      throw error;
    }
  }, [addToast, refetchGrid]);

  return <StabilizedGridContext.Provider value={{
    grids,
    activeGrid,
    loading,
    setActiveGrid,
    updateGrid,
    refetchGrid
  }}>
      {children}
    </StabilizedGridContext.Provider>;
};
export const useStabilizedGrid = () => {
  const context = useContext(StabilizedGridContext);
  if (!context) throw new Error('useStabilizedGrid must be used within StabilizedGridProvider');
  return context;
};
// Stabilized Allocation Context - Fast, conflict-free assignments
interface StabilizedAllocationContextType {
  assignTeacher: (classId: string, lineId: string, teacherCode: string | null) => Promise<void>; // Keep legacy signature as default
  batchAssignTeachers: (assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>) => Promise<void>; // Keep legacy signature as default
  getTeacherSemesterLoad: (teacherId: string, semester: 'semester1' | 'semester2') => {
    allocatedMinutes: number;
    availableMinutes: number;
    utilization: number;
    overloaded: boolean;
  };
  // Multi-grid methods
  assignTeacherMultiGrid?: (gridId: string, classId: string, lineId: string, teacherCode: string | null) => Promise<void>;
  batchAssignTeachersMultiGrid?: (gridId: string, assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>) => Promise<void>;
  // Assignment change notification
  onAssignmentChange?: (callback: () => void) => () => void; // Returns cleanup function
}
const StabilizedAllocationContext = createContext<StabilizedAllocationContextType | undefined>(undefined);
export const StabilizedAllocationProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    addToast
  } = useToast();
  // Teacher loads are per academic year. MultiGridContext already scopes its grid
  // list this way (gridsForYear); the load engine below has to do the same, or it
  // sums every grid ever created. This provider is mounted inside
  // AcademicYearProvider (App.tsx), so the hook is safe to call unguarded.
  const { workingYear } = useAcademicYear();
  const {
    grids,
    activeGrid,
    updateGrid,
    refetchGrid
  } = useStabilizedGrid();
  const {
    staff
  } = useData();
  const { user } = useAuth();
  const assignmentInProgress = useRef(false);

  // Staff code -> display name, for the denormalised teacher_name written into
  // assignment_history. Falls back to the code itself so the audit trail is
  // never blank.
  const resolveStaffName = useCallback(
    (code: string | null | undefined): string | null => {
      if (!code) return null;
      const s = (staff || []).find(
        (x: any) => x.id === code || x.initial === code || x.staff_id === code
      );
      return s?.name || code;
    },
    [staff]
  );

  // Assignment audit rows are no longer written from the browser. They are
  // inserted by pit.set_line_assignment inside the same transaction as the
  // grid write, so the history can never disagree with what was stored, and
  // a failed write leaves no phantom audit row behind (issue #218).
  const pendingAssignments = useRef<Map<string, any>>(new Map());
  const assignmentChangeCallbacks = useRef<Set<() => void>>(new Set());

  // Assignment change notification system
  const onAssignmentChange = useCallback((callback: () => void) => {
    assignmentChangeCallbacks.current.add(callback);
    return () => {
      assignmentChangeCallbacks.current.delete(callback);
    };
  }, []);

  const notifyAssignmentChange = useCallback(() => {
    assignmentChangeCallbacks.current.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in assignment change callback:', error);
      }
    });
  }, []);

  // Clear pending assignments when the active grid changes
  useEffect(() => {
    if (activeGrid) {
      pendingAssignments.current.clear();
    }
  }, [activeGrid?.id]);
  
  const assignTeacherMultiGrid = useCallback(async (gridId: string, classId: string, lineId: string, teacherCode: string | null) => {
    // Find the target grid from all available grids
    let targetGrid: StaffAllocationGrid | null = null;
    
    // First try to find in all available grids
    targetGrid = grids.find(g => g.id === gridId) || null;
    
    // If not found in grids but matches activeGrid, use activeGrid
    if (!targetGrid && gridId === activeGrid?.id) {
      targetGrid = activeGrid;
    }
    
    if (!targetGrid) {
      console.error('❌ StabilizedAllocation: Target grid not found:', {
        requestedGridId: gridId,
        availableGridIds: grids.map(g => g.id),
        activeGridId: activeGrid?.id
      });
      addToast('Assignment failed: Grid not found', 'error');
      return;
    }

    // Remove the blocking check since we want concurrent assignments
    // assignmentInProgress.current = true;

    try {
      const classItem = targetGrid.classes.find(c => c.id === classId);
      if (!classItem) {
        console.error(`❌ Class lookup failed. Looking for: ${classId}`);
        console.error(`   Available class IDs (first 5):`, targetGrid.classes.slice(0, 5).map(c => c.id));
        throw new Error(`Class ${classId} not found`);
      }

      // The previous teacher is no longer read here: pit.set_line_assignment
      // determines it server-side from the stored row, so the audit trail
      // records what was actually replaced rather than what this browser
      // believed was there.

      // Validate line exists in target grid
      const lineExists = targetGrid.lines?.some(line => {
        return typeof line === 'string' ? line === lineId : line?.id === lineId;
      });
      if (!lineExists) {
        console.error(`❌ Line lookup failed. Looking for: ${lineId}`);
        console.error(`   Available lines:`, targetGrid.lines);
        throw new Error(`Line ${lineId} not found in grid ${gridId}`);
      }

      // Write only this one cell, server-side. This deliberately does NOT
      // send the whole class array: doing so let a stale in-memory copy
      // delete assignments it never knew about, silently, which destroyed
      // 212 assignments between 3-8 Sep 2026 (issue #218). The RPC also
      // writes the assignment_history row in the same transaction, so the
      // audit trail can no longer drift from the grid.
      //
      // The pendingAssignments map that used to be merged in here existed to
      // paper over that same race and is no longer needed for this path.
      await setLineAssignment(
        targetGrid.id,
        {
          classId,
          lineId,
          teacherCode,
          teacherName: resolveStaffName(teacherCode),
          className: classItem.className,
          subject: classItem.subjectCode || classItem.className || null,
        },
        { email: (user as any)?.email, id: (user as any)?.id }
      );

      // Refresh the grid to show real-time updates
      await refetchGrid(targetGrid.id);

      // Notify components that assignment has changed
      notifyAssignmentChange();

      // Determine if this is a multi-teacher assignment
      let message = '';
      if (teacherCode === null) {
        message = `✅ Cleared assignment from ${classItem.className} (Line ${lineId})`;
      } else if (teacherCode.startsWith('[') || teacherCode.startsWith('{')) {
        try {
          const assignments = JSON.parse(teacherCode);
          if (Array.isArray(assignments) && assignments.length > 0) {
            // Create a more user-friendly message for multi-teacher assignments
            const teacherCodes = assignments.map(a => a.teacherCode).join(', ');
            message = `✅ Assigned ${assignments.length} teachers (${teacherCodes}) to ${classItem.className} (Line ${lineId})`;
          } else {
            message = `✅ Updated assignment for ${classItem.className} (Line ${lineId})`;
          }
        } catch (e) {
          message = `✅ Updated assignment for ${classItem.className} (Line ${lineId})`;
        }
      } else {
        message = `✅ Assigned ${teacherCode} to ${classItem.className} (Line ${lineId})`;
      }
      addToast(message, 'success');
    } catch (error) {
      console.error('❌ StabilizedAllocation: Assignment failed:', error);
      addToast('Assignment failed', 'error');
      throw error;
    } finally {
      // Remove assignment blocking since we want concurrent assignments
      // assignmentInProgress.current = false;
    }
  }, [grids, activeGrid, addToast, resolveStaffName, user]);

  // Batch assignment function - processes multiple assignments in a single Firebase write
  const batchAssignTeachersMultiGrid = useCallback(async (gridId: string, assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>) => {
    // Find the target grid from all available grids
    let targetGrid: StaffAllocationGrid | null = null;
    
    // First try to find in all available grids
    targetGrid = grids.find(g => g.id === gridId) || null;
    
    // If not found in grids but matches activeGrid, use activeGrid
    if (!targetGrid && gridId === activeGrid?.id) {
      targetGrid = activeGrid;
    }
    
    if (!targetGrid) {
      console.error('❌ StabilizedAllocation: Target grid not found for batch assignment:', {
        requestedGridId: gridId,
        availableGridIds: grids.map(g => g.id),
        activeGridId: activeGrid?.id
      });
      addToast('Batch assignment failed: Grid not found', 'error');
      return;
    }

    try {
      // Create a map of all assignment changes
      const assignmentChanges = new Map<string, Record<string, string | null>>();

      // Process each assignment to build the changes map
      assignments.forEach(({ classId, lineId, teacherCode }) => {
        if (!assignmentChanges.has(classId)) {
          const classItem = activeGrid.classes.find(c => c.id === classId);
          // Start with existing assignments for this class
          assignmentChanges.set(classId, { ...(classItem?.lineAssignments || {}) });
        }

        const classAssignments = assignmentChanges.get(classId)!;
        if (teacherCode === null) {
          delete classAssignments[lineId];
        } else {
          classAssignments[lineId] = teacherCode;
        }
      });

      // Apply all changes to create updated classes array
      // One transaction, cell by cell, server-side: all of these land or none
      // do. As with the single-cell path, the whole class array is never sent,
      // so a stale copy cannot wipe assignments outside this batch (#218).
      // Each cell's audit row is written inside the same transaction.
      await setLineAssignmentsBulk(
        targetGrid.id,
        assignments.map(({ classId, lineId, teacherCode }) => {
          const orig = targetGrid!.classes.find(c => c.id === classId);
          return {
            classId,
            lineId,
            teacherCode,
            teacherName: resolveStaffName(teacherCode),
            className: orig?.className ?? null,
            subject: orig?.subjectCode || orig?.className || null,
          };
        }),
        { email: (user as any)?.email, id: (user as any)?.id }
      );

      // Notify components that assignments have changed
      notifyAssignmentChange();

      // Force a refresh to ensure UI updates
      try {
        await refetchGrid(activeGrid.id);
      } catch (refreshError) {
        console.error('❌ StabilizedAllocation: Manual refresh failed:', refreshError);
      }
      
      addToast(`✅ Successfully assigned ${assignments.length} teachers`, 'success');

    } catch (error) {
      console.error('❌ StabilizedAllocation: BATCH assignment failed:', error);
      addToast('Batch assignment failed', 'error');
      throw error;
    }
  }, [grids, activeGrid, refetchGrid, addToast, resolveStaffName, user]);
  
  const getTeacherSemesterLoad = useCallback((teacherId: string, semester: 'semester1' | 'semester2') => {
    if (!staff) {
      return {
        allocatedMinutes: 0,
        availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        utilization: 0,
        overloaded: false
      };
    }

    // Find the staff member
    const staffMember = staff.find(s => s.id === teacherId || s.initial === teacherId);
    if (!staffMember) {
      return {
        allocatedMinutes: 0,
        availableMinutes: DEFAULT_BASE_MINUTES_PER_FORTNIGHT,
        utilization: 0,
        overloaded: false
      };
    }
    
    // Calculate allocatedMinutes across ALL grids (multi-grid support)
    let allocatedMinutes = 0;
    
    // Aggregate across the grids for the year being planned ONLY. GridsDataContext
    // fetches every grid row regardless of academic_year, so an unfiltered sum made
    // each teacher look pre-loaded with last year's minutes before a single class in
    // the new year had been assigned (issue #165). A grid with no academic_year is
    // still counted rather than silently dropped.
    const candidateGrids = grids.length > 0 ? grids : (activeGrid ? [activeGrid] : []);
    const gridsToCheck = candidateGrids.filter(g => g.year == null || g.year === workingYear);

    gridsToCheck.forEach(grid => {
      // Get semester lines for this grid
      const semesterLines = grid.lines.filter(line => {
        // Handle different line formats - check semester, studyPeriod, or derive from line ID
        return line.semester === semester || 
               line.studyPeriod === semester ||
               (semester === 'semester1' && ['A', 'B', 'C', 'D', 'E', 'F'].includes(line.id)) ||
               (semester === 'semester2' && ['G', 'H', 'I', 'J', 'K', 'L'].includes(line.id));
      });
      const semesterLineIds = semesterLines.map(line => line.id);
      
      // Allocated minutes via the one shared definition (issue #93).
      //
      // This loop used to live here inline. It parsed multi-teacher JSON
      // correctly but had two defects the shared version fixes: it never
      // skipped composite sub-classes (billing a teacher twice for one lesson —
      // 48 assigned sub-classes across the live grids), and it fell back to a
      // hardcoded 440 rather than the grid's own default, which counted each of
      // the 120 assigned 2026 RUAH classes at 440 against a real default of 210.
      allocatedMinutes += computeAllocatedMinutes(grid, staffMember, semesterLineIds);
    });
    // Available minutes via the one shared definition (issue #93).
    //
    // This block used to sum semesterData.allowances inline, filtered only by
    // appliesTo. Two defects: it omitted `both` allowances entirely, and it had
    // no year filter — so after v2.11.0 backfilled every allowance into 2026 AND
    // 2027 it counted each one twice, halving apparent capacity in the Grid
    // Editor's load panels and flagging staff as overloaded who were not.
    const { availableMinutes } = computeAvailableMinutes(staffMember, semester, workingYear);
    const utilization = computeUtilisation(allocatedMinutes, availableMinutes);
    // Check if overloaded
    const overloaded = allocatedMinutes > availableMinutes;
    return {
      allocatedMinutes,
      availableMinutes,
      utilization,
      overloaded
    };
  }, [grids, activeGrid, staff, workingYear]);

  // Legacy methods (now the default interface for backward compatibility)
  const assignTeacher = useCallback(async (classId: string, lineId: string, teacherCode: string | null) => {
    if (!activeGrid) return;
    return assignTeacherMultiGrid(activeGrid.id, classId, lineId, teacherCode);
  }, [assignTeacherMultiGrid, activeGrid]);

  const batchAssignTeachers = useCallback(async (assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>) => {
    if (!activeGrid) return;
    return batchAssignTeachersMultiGrid(activeGrid.id, assignments);
  }, [batchAssignTeachersMultiGrid, activeGrid]);

  return (
    <StabilizedAllocationContext.Provider
      value={{
        assignTeacher,
        batchAssignTeachers,
        getTeacherSemesterLoad,
        assignTeacherMultiGrid,
        batchAssignTeachersMultiGrid,
        onAssignmentChange
      }}
    >
      {children}
    </StabilizedAllocationContext.Provider>
  );
};
export const useStabilizedAllocation = () => {
  const context = useContext(StabilizedAllocationContext);
  if (!context) throw new Error('useStabilizedAllocation must be used within StabilizedAllocationProvider');
  return context;
};